import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, TrendingUp, ClipboardList, GitCompare, Lightbulb, Target, Database, AlertTriangle, Settings2 } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const h = (n: number | null | undefined) => (n == null ? "—" : `${Number(n).toFixed(1)}h`);
const QUALITY_TONE: Record<string, string> = { ok: "good", low_confidence: "warn", insufficient_data: "critical", stale_inputs: "warn", anomalies_present: "warn" };
const PLAN_TONE: Record<string, string> = { draft: "info", reviewed: "warn", approved: "good", superseded: "inactive", archived: "inactive" };

export default function LaborPlanning() {
  const {
    t
  } = useI18n();

  const { modules } = useAuth();
  const [stores, setStores] = useState<any[]>([]);
  const [storeId, setStoreId] = useState("");
  const [tab, setTab] = useState("forecast");

  useEffect(() => {
    api.get("/org/stores").then((r) => { setStores(r.data.stores); if (r.data.stores[0]) setStoreId(r.data.stores[0].id); }).catch(() => {});
  }, []);

  if (modules.labor_planning === false) {
    return (
      <div>
        <PageHeader title="Labor Planning" testId="lp-header" />
        <EmptyState icon={<CalendarClock size={28} />} title="Labor Planning module is inactive"
          description="Enable it in Admin → Modules to build forecasts and labor plans." />
      </div>
    );
  }

  return (
    (<div>
      <PageHeader title="Labor Planning" subtitle="Demand forecasting, labor plans, coverage comparison and staffing signals" testId="lp-header" />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">{t("Store")}</Label>
          <Select value={storeId} onValueChange={setStoreId}>
            <SelectTrigger className="mt-1 w-64" data-testid="lp-store-select"><SelectValue placeholder={t("Select store")} /></SelectTrigger>
            <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{t("Store")}{" "}{s.store_number}· {s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {!storeId ? <LoadingBlock /> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="forecast" data-testid="lp-tab-forecast"><TrendingUp size={14} className="mr-1" />{t("Forecast")}</TabsTrigger>
            <TabsTrigger value="plan" data-testid="lp-tab-plan"><ClipboardList size={14} className="mr-1" />{t("Labor Plan")}</TabsTrigger>
            <TabsTrigger value="compare" data-testid="lp-tab-compare"><GitCompare size={14} className="mr-1" />{t("Coverage vs Plan")}</TabsTrigger>
            <TabsTrigger value="recs" data-testid="lp-tab-recs"><Lightbulb size={14} className="mr-1" />{t("Recommendations")}</TabsTrigger>
            <TabsTrigger value="accuracy" data-testid="lp-tab-accuracy"><Target size={14} className="mr-1" />{t("Accuracy")}</TabsTrigger>
            <TabsTrigger value="inputs" data-testid="lp-tab-inputs"><Database size={14} className="mr-1" />{t("Inputs")}</TabsTrigger>
            <TabsTrigger value="exceptions" data-testid="lp-tab-exceptions"><AlertTriangle size={14} className="mr-1" />{t("Exceptions")}</TabsTrigger>
            <TabsTrigger value="settings" data-testid="lp-tab-settings"><Settings2 size={14} className="mr-1" />{t("Settings")}</TabsTrigger>
          </TabsList>
          <TabsContent value="forecast"><ForecastPanel storeId={storeId} /></TabsContent>
          <TabsContent value="plan"><PlanPanel storeId={storeId} /></TabsContent>
          <TabsContent value="compare"><ComparePanel storeId={storeId} /></TabsContent>
          <TabsContent value="recs"><RecsPanel storeId={storeId} /></TabsContent>
          <TabsContent value="accuracy"><AccuracyPanel storeId={storeId} /></TabsContent>
          <TabsContent value="inputs"><InputsPanel storeId={storeId} /></TabsContent>
          <TabsContent value="exceptions"><ExceptionsPanel /></TabsContent>
          <TabsContent value="settings"><SettingsPanel /></TabsContent>
        </Tabs>
      )}
    </div>)
  );
}

const isoAdd = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };

const ForecastPanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [forecasts, setForecasts] = useState<any[]>([]);
  const [active, setActive] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [start, setStart] = useState(isoAdd(0));
  const [end, setEnd] = useState(isoAdd(6));
  const load = useCallback(() => {
    api.get(`/labor/forecasts?store_id=${storeId}`).then((r) => { setForecasts(r.data.forecasts); setActive(r.data.forecasts.find((f: any) => f.status === "active") || r.data.forecasts[0] || null); }).catch(() => {});
  }, [storeId]);
  useEffect(() => { load(); }, [load]);
  const generate = async () => {
    setBusy(true);
    try { await api.post("/labor/forecasts", { store_id: storeId, horizon_start: start, horizon_end: end }); toast.success("Forecast generated"); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    (<div data-testid="lp-forecast">
      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
        <div><Label className="text-xs">{t("Horizon start")}</Label><Input type="date" className="mt-1 w-40" value={start} onChange={(e) => setStart(e.target.value)} data-testid="lp-fc-start" /></div>
        <div><Label className="text-xs">{t("Horizon end")}</Label><Input type="date" className="mt-1 w-40" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="lp-fc-end" /></div>
        <Button disabled={busy} onClick={generate} data-testid="lp-fc-generate">{t("Generate baseline forecast")}</Button>
      </Card>
      {!active ? <EmptyState icon={<TrendingUp size={24} />} title="No forecast yet" description="Generate a deterministic baseline from recorded demand history." /> : (
        <Card className="p-4" data-testid="lp-fc-detail">
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <div className="font-display font-semibold">v{active.version_no} · {active.method} <span className="text-xs text-muted-foreground">({active.method_version})</span></div>
            <StatusPill status={QUALITY_TONE[active.quality_status] || "info"} testId="lp-fc-quality">{active.quality_status.replace("_", " ")}</StatusPill>
            <span className="text-xs text-muted-foreground">{t("history")}{" "}{active.inputs_snapshot?.history_points}{" "}{t("pts · lookback")}{" "}{active.params?.lookback_weeks}{t("w · intraday")}{" "}{active.inputs_snapshot?.intraday_profile_basis}</span>
          </div>
          <table className="w-full text-sm" data-testid="lp-fc-table">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="p-1">{t("Date")}</th><th className="p-1">DOW</th><th className="p-1">{t("Forecast")}</th><th className="p-1">{t("Basis")}</th><th className="p-1">{t("Pts")}</th></tr></thead>
            <tbody>{active.daily.map((d: any) => <tr key={d.date} className="border-t border-border"><td className="p-1">{d.date}</td><td className="p-1">{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][d.dow]}</td><td className="p-1 font-medium">{d.value}</td><td className="p-1 text-xs text-muted-foreground">{d.basis}</td><td className="p-1">{d.points}</td></tr>)}</tbody>
          </table>
          <div className="mt-2 text-[11px] text-muted-foreground">{t("Deterministic same-weekday moving average. Excluded dates:")}{" "}{active.exclusions?.length ? active.exclusions.join(", ") : t("none")}{t(". Repeatable from the recorded inputs above.")}</div>
          {forecasts.length > 1 && <div className="mt-2 text-xs text-muted-foreground" data-testid="lp-fc-versions">{forecasts.length}{" "}{t("versions on record (prior versions preserved).")}</div>}
        </Card>
      )}
      {active && <ScenariosCard forecast={active} />}
    </div>)
  );
};

const ScenariosCard: React.FC<{ forecast: any }> = ({ forecast }) => {
  const {
    t
  } = useI18n();

  const [scenarios, setScenarios] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [open, setOpen] = useState<any>(null);
  const [tgt, setTgt] = useState(forecast.daily[0]?.date || "");
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  useEffect(() => { setScenarios([]); }, [forecast.id]);
  const create = async () => {
    if (!name.trim()) { toast.error("Name the scenario"); return; }
    try { const r = await api.post("/labor/scenarios", { forecast_id: forecast.id, name }); setScenarios((s) => [...s, r.data.scenario]); setName(""); toast.success("Scenario created"); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addOverride = async () => {
    if (!reason.trim() || !val) { toast.error("Value and reason are required"); return; }
    try {
      const r = await api.post(`/labor/scenarios/${open.id}/overrides`, { target_date: tgt, new_value: Number(val), reason, evidence: evidence || null });
      setScenarios((list) => list.map((s) => (s.id === open.id ? r.data.scenario : s)));
      setOpen(null); setVal(""); setReason(""); setEvidence(""); toast.success("Override recorded (original preserved)");
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    (<Card className="mt-4 p-4" data-testid="lp-scenarios">
      <div className="mb-2 font-display text-sm font-semibold">{t("What-if scenarios & overrides")}</div>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div><Label className="text-xs">{t("New scenario")}</Label><Input className="mt-1 w-48" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("e.g. Heatwave")} data-testid="lp-scenario-name" /></div>
        <Button size="sm" onClick={create} data-testid="lp-scenario-create">{t("Create scenario")}</Button>
      </div>
      {scenarios.length === 0 ? <div className="text-xs text-muted-foreground">{t(
        "No scenarios yet. Overrides preserve the original forecast value, reason, evidence and author."
      )}</div> : (
        <div className="space-y-2" data-testid="lp-scenario-list">
          {scenarios.map((s) => (
            <div key={s.id} className="rounded-md border border-border p-2 text-sm" data-testid={`lp-scenario-${s.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">{s.name} <span className="text-xs text-muted-foreground">v{s.version_no}</span></span>
                <Button size="sm" variant="outline" onClick={() => { setOpen(s); setTgt(forecast.daily[0]?.date); }} data-testid={`lp-scenario-override-${s.id}`}>{t("Add override")}</Button>
              </div>
              {s.overrides?.map((o: any, i: number) => (
                <div key={i} className="mt-1 text-xs text-muted-foreground" data-testid={`lp-override-${s.id}-${i}`}>{o.target_date}: <s>{o.original_value}</s> → <b>{o.new_value}</b> · {o.reason}{o.evidence ? ` (${o.evidence})` : ""}</div>
              ))}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <DialogContent data-testid="lp-override-dialog">
          <DialogHeader><DialogTitle>{t("Override —")}{" "}{open?.name}</DialogTitle><DialogDescription>{t(
            "Adjust a forecast value with a reason; the original value and author are preserved in history."
          )}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">{t("Date")}</Label>
              <Select value={tgt} onValueChange={setTgt}><SelectTrigger className="mt-1" data-testid="lp-override-date"><SelectValue /></SelectTrigger>
                <SelectContent>{forecast.daily.map((d: any) => <SelectItem key={d.date} value={d.date}>{d.date}{" "}{t("(base")}{" "}{d.value})</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">{t("New value")}</Label><Input type="number" className="mt-1" value={val} onChange={(e) => setVal(e.target.value)} data-testid="lp-override-value" /></div>
            <div><Label className="text-xs">{t("Reason (required)")}</Label><Input className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="lp-override-reason" /></div>
            <div><Label className="text-xs">{t("Evidence")}</Label><Input className="mt-1" value={evidence} onChange={(e) => setEvidence(e.target.value)} data-testid="lp-override-evidence" /></div>
          </div>
          <DialogFooter><Button onClick={addOverride} data-testid="lp-override-submit">{t("Record override")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>)
  );
};

const PlanPanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const [plans, setPlans] = useState<any[]>([]);
  const [sel, setSel] = useState<any>(null);
  const load = useCallback(() => {
    api.get(`/labor/plans?store_id=${storeId}`).then((r) => { setPlans(r.data.plans); setSel(r.data.plans[0] || null); }).catch(() => {});
  }, [storeId]);
  useEffect(() => { load(); }, [load]);
  const build = async () => {
    try {
      const fr = await api.get(`/labor/forecasts?store_id=${storeId}`);
      const f = fr.data.forecasts.find((x: any) => x.status === "active") || fr.data.forecasts[0];
      if (!f) { toast.error("Generate a forecast first"); return; }
      await api.post("/labor/plans", { forecast_id: f.id, period_start: f.horizon.start, period_end: f.horizon.end });
      toast.success("Draft labor plan created"); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const transition = async (to: string) => {
    try { await api.post(`/labor/plans/${sel.id}/transition`, { to }); toast.success(tl("Plan updated")); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    (<div data-testid="lp-plan">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button onClick={build} data-testid="lp-plan-build">{t("Build draft plan from active forecast")}</Button>
        {plans.length > 0 && (
          <Select value={sel?.id || ""} onValueChange={(v) => setSel(plans.find((p) => p.id === v))}>
            <SelectTrigger className="w-72" data-testid="lp-plan-select"><SelectValue /></SelectTrigger>
            <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>v{p.version_no} · {p.period.start}→{p.period.end} · {p.status}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      {!sel ? <EmptyState icon={<ClipboardList size={24} />} title="No labor plan" description="Build a draft from the active forecast." /> : (
        <Card className="p-4" data-testid="lp-plan-detail">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <div className="font-display font-semibold">{t("Plan v")}{sel.version_no}</div>
            <StatusPill status={PLAN_TONE[sel.status] || "info"} testId="lp-plan-status">{stat(sel.status)}</StatusPill>
            <span className="text-xs text-muted-foreground">{t("required")}{" "}{h(sel.computed.totals.required_hours)}{" "}{t("· budget")}{" "}{sel.budget_hours ? h(sel.budget_hours) : "—"}{" "}{t("· variance")}{" "}{sel.computed.budget_variance != null ? h(sel.computed.budget_variance) : "—"}</span>
            <span className="text-xs text-muted-foreground">{t("from forecast v")}{sel.forecast_version}{sel.scenario_id ? ` · ${tl("scenario")} v${sel.scenario_version}` : ""}</span>
          </div>
          <div className="mb-3 flex flex-wrap gap-2">
            {sel.status === "draft" && <Button size="sm" variant="outline" onClick={() => transition("reviewed")} data-testid="lp-plan-review">{t("Mark reviewed")}</Button>}
            {sel.status === "reviewed" && hasPerm("lp.approve") && <Button size="sm" onClick={() => transition("approved")} data-testid="lp-plan-approve">{t("Approve")}</Button>}
            {sel.status === "approved" && <Button size="sm" variant="outline" onClick={() => transition("archived")} data-testid="lp-plan-archive">{t("Archive")}</Button>}
          </div>
          <table className="w-full text-sm" data-testid="lp-plan-table">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="p-1">{t("Date")}</th><th className="p-1">{t("Demand")}</th><th className="p-1">{t("Variable")}</th><th className="p-1">{t("Fixed")}</th><th className="p-1">{t("Required")}</th><th className="p-1">{t("Roles")}</th><th className="p-1">{t("Peak")}</th></tr></thead>
            <tbody>{sel.computed.daily.map((d: any) => <tr key={d.date} className="border-t border-border"><td className="p-1">{d.date}</td><td className="p-1">{d.demand}</td><td className="p-1">{h(d.components.variable_hours)}</td><td className="p-1">{h(d.components.fixed_hours)}</td><td className="p-1 font-medium">{h(d.required_hours)}</td><td className="p-1 text-xs">{d.role_breakdown.map((r: any) => `${r.role} ${h(r.hours)}`).join(", ")}</td><td className="p-1 text-xs">{d.peak_interval}</td></tr>)}</tbody>
          </table>
          <div className="mt-2 text-[11px] text-muted-foreground">{t("Transparent: required = demand ÷")}{" "}{sel.assumptions.productivity.units_per_labor_hour}/labor-hr + fixed tasks, floored at minimum staffing. Lifecycle: draft → reviewed → approved → archived.</div>
        </Card>
      )}
    </div>)
  );
};

const ComparePanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [plans, setPlans] = useState<any[]>([]);
  const [pid, setPid] = useState("");
  const [cmp, setCmp] = useState<any>(null);
  const [obs, setObs] = useState<any>(null);
  useEffect(() => { api.get(`/labor/plans?store_id=${storeId}`).then((r) => { setPlans(r.data.plans); if (r.data.plans[0]) setPid(r.data.plans[0].id); }); }, [storeId]);
  useEffect(() => {
    if (!pid) return;
    api.get(`/labor/plans/${pid}/compare`).then((r) => setCmp(r.data)).catch(() => setCmp(null));
    api.get(`/labor/plans/${pid}/observations`).then((r) => setObs(r.data)).catch(() => setObs(null));
  }, [pid]);
  return (
    (<div data-testid="lp-compare">
      {plans.length > 0 && <Select value={pid} onValueChange={setPid}><SelectTrigger className="mb-4 w-72" data-testid="lp-compare-select"><SelectValue /></SelectTrigger><SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>v{p.version_no} · {p.status}</SelectItem>)}</SelectContent></Select>}
      {!cmp ? <LoadingBlock /> : (
        <Card className="p-4">
          <table className="w-full text-sm" data-testid="lp-compare-table">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="p-1">{t("Date")}</th><th className="p-1">{t("Required")}</th><th className="p-1">{t("Scheduled")}</th><th className="p-1">{t("Open")}</th><th className="p-1">{t("Redeployed")}</th><th className="p-1">{t("Actual")}</th><th className="p-1">{t("Sched−Req")}</th></tr></thead>
            <tbody>{cmp.rows.map((r: any) => <tr key={r.date} className="border-t border-border"><td className="p-1">{r.date}</td><td className="p-1 font-medium">{h(r.required_hours)}</td><td className="p-1">{h(r.scheduled)}</td><td className="p-1">{h(r.offered_open)}</td><td className="p-1">{h(r.redeployed)}</td><td className="p-1">{h(r.actual_hours)}</td><td className={`p-1 ${r.sched_vs_required < 0 ? "text-status-warn" : "text-status-good"}`}>{h(r.sched_vs_required)}</td></tr>)}</tbody>
            <tfoot><tr className="border-t-2 border-border font-medium"><td className="p-1">{t("Total")}</td><td className="p-1">{h(cmp.totals.required)}</td><td className="p-1">{h(cmp.totals.scheduled)}</td><td className="p-1">{h(cmp.totals.offered_open)}</td><td className="p-1">{h(cmp.totals.redeployed)}</td><td className="p-1">{h(cmp.totals.actual)}</td><td className="p-1" /></tr></tfoot>
          </table>
          <div className="mt-1 text-[11px] text-muted-foreground">{t("Reads the accepted Scheduling & Time-and-Attendance records (forecast v")}{cmp.forecast_version}{t("). No labor data is duplicated here.")}</div>
        </Card>
      )}
      {obs && (
        <Card className="mt-4 p-4" data-testid="lp-observations">
          <div className="mb-2 font-display text-sm font-semibold">{t("Staffing observations")}</div>
          <div className="grid gap-3 text-sm sm:grid-cols-3">
            <div><div className="text-xs font-semibold text-status-warn">{t("Undercoverage (")}{obs.undercoverage.length})</div>{obs.undercoverage.map((u: any) => <div key={u.date} className="text-xs">{u.date}: {h(u.gap_hours)}{!u.has_schedule ? t("(no schedule)") : ""}</div>)}</div>
            <div><div className="text-xs font-semibold text-status-good">{t("Overcoverage (")}{obs.overcoverage.length})</div>{obs.overcoverage.map((o: any) => <div key={o.date} className="text-xs">{o.date}: +{h(o.surplus_hours)}</div>)}</div>
            <div><div className="text-xs font-semibold">{t("Role/skill gaps (")}{obs.role_skill_gaps.length})</div>{obs.role_skill_gaps.map((g: any) => <div key={g.date} className="text-xs">{g.date}: {g.missing_roles.join(", ")}</div>)}</div>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">{obs.note}</div>
        </Card>
      )}
    </div>)
  );
};

const RecsPanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [plans, setPlans] = useState<any[]>([]);
  const [pid, setPid] = useState("");
  const [recs, setRecs] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => { if (pid) api.get(`/labor/plans/${pid}/recommendations`).then((r) => setRecs(r.data.recommendations)).catch(() => setRecs([])); }, [pid]);
  useEffect(() => { api.get(`/labor/plans?store_id=${storeId}`).then((r) => { setPlans(r.data.plans); if (r.data.plans[0]) setPid(r.data.plans[0].id); }); }, [storeId]);
  useEffect(() => { load(); }, [load]);
  const generate = async () => { try { const r = await api.post(`/labor/plans/${pid}/recommendations`); toast.success(`${r.data.created} draft recommendation(s)`); load(); } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const decide = async (rec: any, action: string, extra: any = {}) => {
    if (action === "reject" && !note.trim()) { toast.error("A note is required to reject"); return; }
    try { const r = await api.post(`/labor/recommendations/${rec.id}/decision`, { action, note: note || null, ...extra }); toast.success(action === "accept" || action === "modify" ? tl("Open shift created") : "Rejected"); setEdit(null); setNote(""); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    (<div data-testid="lp-recs">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {plans.length > 0 && <Select value={pid} onValueChange={setPid}><SelectTrigger className="w-72" data-testid="lp-recs-select"><SelectValue /></SelectTrigger><SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>v{p.version_no} · {p.status}</SelectItem>)}</SelectContent></Select>}
        <Button onClick={generate} data-testid="lp-recs-generate">{t("Generate draft recommendations")}</Button>
      </div>
      <div className="mb-2 rounded-md border border-status-warn/40 bg-status-warn/5 p-2 text-xs text-muted-foreground">{t("Recommendations are drafts. Accepting creates an")}{" "}<b>{t("open shift")}</b>{" "}{t("only — it never assigns an employee or publishes a schedule.")}</div>
      {recs.length === 0 ? <EmptyState icon={<Lightbulb size={24} />} title="No recommendations" description="Generate drafts from the plan's under-covered days." /> : (
        <div className="space-y-2" data-testid="lp-recs-list">
          {recs.map((r) => (
            <Card key={r.id} className="p-3 text-sm" data-testid={`lp-rec-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium">{r.date} · {r.role} · {r.suggested_start}–{r.suggested_end}</div>
                <StatusPill status={r.status === "draft" ? "info" : r.status === "rejected" ? "critical" : "good"}>{stat(r.status)}</StatusPill>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{r.rationale}</div>
              {r.status === "draft" && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => decide(r, "accept")} data-testid={`lp-rec-accept-${r.id}`}>{t("Accept → open shift")}</Button>
                  <Button size="sm" variant="outline" onClick={() => { setEdit(r); setNote(""); }} data-testid={`lp-rec-modify-${r.id}`}>{t("Modify")}</Button>
                  <Button size="sm" variant="outline" onClick={() => { setEdit({ ...r, _reject: true }); setNote(""); }} data-testid={`lp-rec-reject-${r.id}`}>{t("Reject")}</Button>
                </div>
              )}
              {r.decision?.resulting_shift_id && <div className="mt-1 text-[11px] text-status-good" data-testid={`lp-rec-shift-${r.id}`}>{t("Open shift created in Workforce.")}</div>}
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent data-testid="lp-rec-dialog">
          <DialogHeader><DialogTitle>{edit?._reject ? t("Reject recommendation") : t("Modify & accept")}</DialogTitle><DialogDescription>{edit?._reject ? t("Provide a reason for rejecting this draft recommendation.") : t(
            "Review and adjust before accepting; accepting creates an open, unassigned shift only."
          )}</DialogDescription></DialogHeader>
          {edit?._reject ? (
            <div><Label className="text-xs">{t("Reason (required)")}</Label><Textarea className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lp-rec-reject-note" /></div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">{t("Start")}</Label><Input className="mt-1" defaultValue={edit?.suggested_start} id="lp-mod-start" data-testid="lp-rec-mod-start" /></div>
              <div><Label className="text-xs">{t("End")}</Label><Input className="mt-1" defaultValue={edit?.suggested_end} id="lp-mod-end" data-testid="lp-rec-mod-end" /></div>
            </div>
          )}
          <DialogFooter>
            {edit?._reject
              ? <Button onClick={() => decide(edit, "reject")} data-testid="lp-rec-reject-submit">{t("Reject")}</Button>
              : <Button onClick={() => decide(edit, "modify", { start: (document.getElementById("lp-mod-start") as HTMLInputElement)?.value, end: (document.getElementById("lp-mod-end") as HTMLInputElement)?.value })} data-testid="lp-rec-modify-submit">{t("Accept modified → open shift")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>)
  );
};

const AccuracyPanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [acc, setAcc] = useState<any>(null);
  const [ctx, setCtx] = useState<any>(null);
  useEffect(() => {
    api.get(`/labor/accuracy?store_id=${storeId}`).then((r) => setAcc(r.data)).catch(() => setAcc(null));
    api.get(`/labor/context?store_id=${storeId}&start=${isoAdd(-30)}&end=${isoAdd(0)}`).then((r) => setCtx(r.data)).catch(() => setCtx(null));
  }, [storeId]);
  return (
    (<div data-testid="lp-accuracy">
      <Card className="p-4" data-testid="lp-accuracy-card">
        <div className="mb-2 font-display text-sm font-semibold">{t("Forecast vs actual")}</div>
        {!acc ? <LoadingBlock /> : acc.metrics.n === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="lp-accuracy-empty">{t(
            "No comparable days yet — actuals will be matched to forecasts as demand is recorded."
          )}</div>
        ) : (
          <div className="flex flex-wrap gap-6 text-sm">
            <div><div className="text-2xl font-bold">{acc.metrics.mape ?? "—"}%</div><div className="text-xs text-muted-foreground">{acc.labels.mape}</div></div>
            <div><div className="text-2xl font-bold">{acc.metrics.mae}</div><div className="text-xs text-muted-foreground">{acc.labels.mae}</div></div>
            <div><div className="text-2xl font-bold">{acc.metrics.bias}</div><div className="text-xs text-muted-foreground">{acc.labels.bias}</div></div>
            <div><div className="text-2xl font-bold">{acc.metrics.n}</div><div className="text-xs text-muted-foreground">{t("days compared")}</div></div>
          </div>
        )}
      </Card>
      <Card className="mt-4 p-4" data-testid="lp-context">
        <div className="mb-2 font-display text-sm font-semibold">{t("Operational context")}</div>
        {!ctx ? <LoadingBlock /> : (ctx.items.length === 0 && ctx.closures.length === 0) ? (
          <div className="text-sm text-muted-foreground">{t("No linked events in this window.")}</div>
        ) : (
          <div className="space-y-1 text-sm">
            {ctx.items.map((i: any, n: number) => <div key={n} className="text-xs"><span className="font-medium">{i.date}</span> · {i.type.replace("_", " ")}: {i.detail}</div>)}
            {ctx.closures.map((c: any, n: number) => <div key={`c${n}`} className="text-xs"><span className="font-medium">{c.date}</span>{" "}{t("· early closure")}</div>)}
          </div>
        )}
        {ctx && <div className="mt-2 text-[11px] text-muted-foreground">{ctx.note}</div>}
      </Card>
    </div>)
  );
};

const InputsPanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [data, setData] = useState<any>(null);
  const [bdate, setBdate] = useState(isoAdd(-1));
  const [val, setVal] = useState("");
  const load = useCallback(() => { api.get(`/labor/inputs?store_id=${storeId}&start=${isoAdd(-30)}&end=${isoAdd(0)}`).then((r) => setData(r.data)).catch(() => {}); }, [storeId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { try { await api.post("/labor/inputs", { store_id: storeId, business_date: bdate, value: Number(val) }); toast.success("Input saved"); setVal(""); load(); } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); } };
  if (!data) return <LoadingBlock />;
  const s = data.states;
  return (
    (<div data-testid="lp-inputs">
      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
        <div><Label className="text-xs">{t("Business date")}</Label><Input type="date" className="mt-1 w-40" value={bdate} onChange={(e) => setBdate(e.target.value)} data-testid="lp-input-date" /></div>
        <div><Label className="text-xs">{t("Transactions")}</Label><Input type="number" className="mt-1 w-32" value={val} onChange={(e) => setVal(e.target.value)} data-testid="lp-input-value" /></div>
        <Button disabled={!val} onClick={add} data-testid="lp-input-add">{t("Record actual")}</Button>
      </Card>
      <Card className="p-4" data-testid="lp-input-states">
        <div className="mb-2 font-display text-sm font-semibold">{t("Data quality")}</div>
        <div className="flex flex-wrap gap-2 text-xs">
          <StatusPill status={s.insufficient_data ? "critical" : "good"}>{t("history")}{" "}{s.history_points}{" "}{t("pts")}{s.insufficient_data ? t("(insufficient)") : ""}</StatusPill>
          <StatusPill status={s.stale ? "warn" : "good"}>{s.stale ? `stale (latest ${s.latest_actual})` : t("fresh")}</StatusPill>
          <StatusPill status={s.missing_dates.length ? "warn" : "good"}>{s.missing_dates.length}{" "}{t("missing day(s)")}</StatusPill>
          <StatusPill status={s.anomalies.length ? "warn" : "good"}>{s.anomalies.length}{" "}{t("anomal")}{s.anomalies.length === 1 ? "y" : t("ies")}</StatusPill>
          <StatusPill status={s.incomplete_intraday_dates.length ? "warn" : "good"}>{s.incomplete_intraday_dates.length}{" "}{t("incomplete intraday")}</StatusPill>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">{data.inputs.length}{" "}{t("input record(s) with provenance in the last 30 days.")}</div>
      </Card>
    </div>)
  );
};

const ExceptionsPanel: React.FC = () => {
  const {
    t
  } = useI18n();

  const [data, setData] = useState<any>(null);
  useEffect(() => { api.get("/labor/exceptions").then((r) => setData(r.data)).catch(() => setData({ available: false, exceptions: [] })); }, []);
  if (!data) return <LoadingBlock />;
  if (!data.available) return <EmptyState icon={<AlertTriangle size={24} />} title="Unavailable" description={data.reason || "Labor Planning is unavailable."} />;
  return (
    (<div data-testid="lp-exceptions">
      {data.exceptions.length === 0 ? <EmptyState icon={<AlertTriangle size={24} />} title="No exceptions" description="Demand inputs and plans are healthy across your scope." /> : (
        <div className="space-y-2">{data.exceptions.map((e: any, n: number) => (
          <Card key={n} className="flex items-center justify-between p-3 text-sm">
            <div><span className="font-medium">{t("Store")}{" "}{e.store_number}</span> · {e.detail}</div>
            <StatusPill status={e.severity === "warn" ? "warn" : "info"}>{e.code.replace(/_/g, " ")}</StatusPill>
          </Card>
        ))}</div>
      )}
    </div>)
  );
};

const SettingsPanel: React.FC = () => {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const [cfg, setCfg] = useState<any>(null);
  const canEdit = hasPerm("lp.manage");
  useEffect(() => { api.get("/labor/config").then((r) => setCfg(r.data.config)).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/labor/config", { config: cfg }); toast.success("Settings saved"); } catch (e: any) { toast.error(e?.response?.data?.detail || "Not authorized"); } };
  if (!cfg) return <LoadingBlock />;
  return (
    (<Card className="max-w-2xl p-4" data-testid="lp-settings">
      <div className="mb-3 font-display text-sm font-semibold">{t("Labor planning assumptions")}</div>
      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <div><Label className="text-xs">{t("Units / labor-hour")}</Label><Input type="number" className="mt-1" value={cfg.productivity.units_per_labor_hour} onChange={(e) => setCfg((c: any) => ({ ...c, productivity: { ...c.productivity, units_per_labor_hour: Number(e.target.value) } }))} disabled={!canEdit} data-testid="lp-cfg-uplh" /></div>
        <div><Label className="text-xs">{t("Min daily hours")}</Label><Input type="number" className="mt-1" value={cfg.min_staffing.min_daily_hours} onChange={(e) => setCfg((c: any) => ({ ...c, min_staffing: { ...c.min_staffing, min_daily_hours: Number(e.target.value) } }))} disabled={!canEdit} data-testid="lp-cfg-mindaily" /></div>
        <div><Label className="text-xs">{t("Lookback weeks")}</Label><Input type="number" className="mt-1" value={cfg.forecast.lookback_weeks} onChange={(e) => setCfg((c: any) => ({ ...c, forecast: { ...c.forecast, lookback_weeks: Number(e.target.value) } }))} disabled={!canEdit} data-testid="lp-cfg-lookback" /></div>
        <div><Label className="text-xs">{t("Open")}</Label><Input className="mt-1" value={cfg.coverage_window.open} onChange={(e) => setCfg((c: any) => ({ ...c, coverage_window: { ...c.coverage_window, open: e.target.value } }))} disabled={!canEdit} data-testid="lp-cfg-open" /></div>
        <div><Label className="text-xs">{t("Close")}</Label><Input className="mt-1" value={cfg.coverage_window.close} onChange={(e) => setCfg((c: any) => ({ ...c, coverage_window: { ...c.coverage_window, close: e.target.value } }))} disabled={!canEdit} data-testid="lp-cfg-close" /></div>
        <div><Label className="text-xs">{t("Stale threshold (days)")}</Label><Input type="number" className="mt-1" value={cfg.thresholds.stale_days} onChange={(e) => setCfg((c: any) => ({ ...c, thresholds: { ...c.thresholds, stale_days: Number(e.target.value) } }))} disabled={!canEdit} data-testid="lp-cfg-stale" /></div>
      </div>
      <div className="mt-3 text-[11px] text-muted-foreground">{t(
        "Assumptions are transparent and versioned into each plan snapshot. No automated assignment or publishing occurs."
      )}</div>
      {canEdit ? <Button className="mt-3" onClick={save} data-testid="lp-cfg-save">{t("Save settings")}</Button> : <div className="mt-3 text-xs text-muted-foreground">{t("Read-only.")}</div>}
    </Card>)
  );
};
